/**
 * About Page - ProcMatrix Agentic Assistant
 */
import { useState } from 'react';
import Layout from '@/components/Layout';
import { 
  Bot, 
  FileSearch, 
  FileText, 
  Zap, 
  Shield, 
  Settings2,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Cpu,
  Brain,
  Info,
  Gauge,
  Target,
  Lock,
  ShieldCheck,
  Database,
  Eye,
  KeyRound,
  FolderTree
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const About = () => {
  const navigate = useNavigate();

  const agents = [
    {
      name: 'Intake Agent',
      description: 'Parses and understands your procurement requirements',
      icon: FileSearch,
      color: 'bg-blue-500'
    },
    {
      name: 'Question Generator',
      description: 'Creates contextual business and risk questions',
      icon: Brain,
      color: 'bg-purple-500'
    },
    {
      name: 'Refinement Agent',
      description: 'Refines requirements based on your answers',
      icon: Settings2,
      color: 'bg-amber-500'
    },
    {
      name: 'Consistency Validator',
      description: 'Flags discrepancies in dates and durations',
      icon: Shield,
      color: 'bg-green-500'
    },
    {
      name: 'RFP Generator',
      description: 'Drafts professional, export-ready RFP documents',
      icon: FileText,
      color: 'bg-indigo-500'
    },
    {
      name: 'Risk Intelligence Agent',
      description: 'Generates risk intelligence based on policy frameworks',
      icon: Zap,
      color: 'bg-red-500'
    }
  ];

  const aiModels = [
    { name: 'OpenAI GPT', description: 'Industry-leading language understanding', logo: '🤖' },
    { name: 'Anthropic Claude', description: 'Advanced reasoning capabilities', logo: '🧠' },
    { name: 'Google Gemini', description: 'Multimodal intelligence', logo: '✨' }
  ];

  const betaScopeItems = [
    'AI-assisted requirement analysis from user-provided descriptions and supporting documents.',
    'Generation of structured procurement packages and supplier-ready RFP documents in Microsoft Word (.docx) format.',
    'Analysis of uploaded supporting documents to assist requirement understanding. For best results, include critical procurement information in the requirement description or provide concise supporting extracts.',
    'AI-assisted line-item generation using uploaded Bills of Quantities (BOQs), optimized for procurement schedules of up to approximately 50 line items.',
    'A single buyer organization profile for document branding and configuration.'
  ];

  return (
    <Layout>
      <div className="p-6 md:p-12 max-w-5xl mx-auto">
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055FF]/10 text-[#0055FF] rounded-full text-sm font-medium mb-6">
            <Bot className="w-4 h-4" />
            Powered by AI Agents
          </div>
          
          <h1 className="mb-2 leading-tight">
            <img src="/brand/logo-full.svg" alt="ProcMatrix" className="h-20 mx-auto" />
          </h1>
          <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
            <h2 className="text-xl md:text-lg lg:text-2xl font-semibold text-zinc-900 md:whitespace-nowrap">
              Enterprise Procurement Knowledge. AI Guidance. Human Control.
            </h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
              Beta
            </span>
          </div>

          <p className="text-lg text-zinc-600 max-w-2xl mx-auto mb-8">
            Transform business requirements into procurement-ready RFPs using company knowledge, procurement expertise and AI-assisted guidance.
          </p>

          <Button 
            onClick={() => navigate('/new')}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-8 py-6 text-lg"
          >
            Start New Procurement <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Why ProcMatrix Section */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl p-8 md:p-12 mb-12 text-white">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#0055FF] rounded-xl">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-3">Why ProcMatrix</h2>
              <p className="text-xl text-zinc-300 leading-relaxed">
                ProcMatrix empowers business users and procurement teams with AI-assisted guidance to improve requirement quality, reduce rework, and generate procurement-ready RFPs while keeping buyers in control.
              </p>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-zinc-900 mb-3">How It Works</h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              ProcMatrix isn&apos;t just a database—it&apos;s a team of specialized AI agents working together to analyze your requirements and generate professional procurement documents.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((agent, index) => (
              <div 
                key={agent.name}
                className="bg-white border border-zinc-200 rounded-xl p-6 hover:shadow-lg hover:border-zinc-300 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 ${agent.color} rounded-lg`}>
                    <agent.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-zinc-400 font-medium mb-1">Agent {index + 1}</div>
                    <h3 className="font-semibold text-zinc-900 mb-1">{agent.name}</h3>
                    <p className="text-sm text-zinc-600">{agent.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-zinc-50 border border-zinc-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Cpu className="w-6 h-6 text-[#0055FF] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-zinc-900 mb-2">The Agentic Difference</h3>
                <p className="text-zinc-600">
                  Unlike traditional procurement tools that simply store data, ProcMatrix&apos;s agents actively <strong>analyze</strong> your intake documents, <strong>generate</strong> contextual questions based on your risk policies, <strong>validate</strong> consistency across your inputs, and <strong>draft</strong> professional RFP documents—all automatically. Each agent specializes in a specific task, working together like a well-coordinated team.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Policy & Taxonomy Foundation Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-600 rounded-lg">
                <Settings2 className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900">Built on Standards, Ready to Customize</h2>
            </div>
            
            <p className="text-zinc-700 mb-8">
              ProcMatrix agents are trained on industry-standard frameworks that can be tailored to match your organization&apos;s specific needs.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Procurement Policy */}
              <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-emerald-100">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-zinc-900">Procurement & Risk Policy</h3>
                </div>
                <p className="text-sm text-zinc-600 mb-4">
                  Our agents operate based on a standard procurement policy and risk intelligence framework covering:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-zinc-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>8 risk domains with weighted scoring factors</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-zinc-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Compliance and regulatory guidelines</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-zinc-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Vendor evaluation criteria</span>
                  </li>
                </ul>
              </div>

              {/* Category Taxonomy */}
              <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-emerald-100">
                <div className="flex items-center gap-3 mb-4">
                  <FolderTree className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-zinc-900">Category Taxonomy</h3>
                </div>
                <p className="text-sm text-zinc-600 mb-4">
                  Procurement categories are organized using a standard taxonomy structure including:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-zinc-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>UNSPSC-aligned category codes</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-zinc-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>3-level hierarchy (L1, L2, L3)</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-zinc-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Goods vs. Services classification</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-emerald-200">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-700">
                  <strong>Customization:</strong> Upload your organization&apos;s procurement policy, risk framework, and category taxonomy via the Master Data Management section. The agents will automatically adapt their behavior to align with your specific requirements and terminology.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Transparency & Verification Section */}
        <div className="mb-16">
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-zinc-200 rounded-lg flex-shrink-0">
                <Info className="w-5 h-5 text-zinc-600" />
              </div>
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-zinc-900">How ProcMatrix Works — and What to Verify</h2>
                <p className="text-zinc-600">
                  ProcMatrix uses AI to accelerate your procurement workflow, but every output is grounded in your organization&apos;s own master data — the category taxonomy and scope elements, procurement and risk policies, delivery locations, units of measure, and currency/exchange rates your administrators maintain. The quality and completeness of that master data directly shapes what ProcMatrix can produce.
                </p>
                <p className="text-zinc-600">
                  ProcMatrix&apos;s suggestions — category classification, generated questions, scope coverage, and risk intelligence — are AI-generated starting points for your review, not final determinations. You remain the decision-maker. In particular, please verify the category and the requirement line items before generating your RFP: a procurement that spans both goods and services (for example, supply-and-install work) is categorized at the individual line-item level, where you can confirm or adjust each line.
                </p>
                <p className="text-zinc-600">
                  If a category, unit, or location you need isn&apos;t available, ask your administrator to update the relevant master data — ProcMatrix can only work within the masters it&apos;s given.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Continuous Learning Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-600 rounded-lg">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900">Continuous Learning & Model Training</h2>
            </div>
            
            <p className="text-zinc-700 mb-8">
              ProcMatrix gets smarter with every use. Our AI learns from user feedback to generate increasingly accurate and relevant RFPs tailored to your organization&apos;s style.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Step 1 */}
              <div className="bg-white/80 backdrop-blur rounded-xl p-5 border border-purple-100 relative">
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <h3 className="font-semibold text-zinc-900 mb-2 mt-2">Generate & Download</h3>
                <p className="text-sm text-zinc-600">
                  AI generates your RFP based on requirements, policies, and learned patterns from your category.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-white/80 backdrop-blur rounded-xl p-5 border border-purple-100 relative">
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <h3 className="font-semibold text-zinc-900 mb-2 mt-2">Share Your Feedback</h3>
                <p className="text-sm text-zinc-600">
                  After downloading, tell us what you typically change—scope, questions, terminology, formatting, etc.
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-white/80 backdrop-blur rounded-xl p-5 border border-purple-100 relative">
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <h3 className="font-semibold text-zinc-900 mb-2 mt-2">AI Learns & Improves</h3>
                <p className="text-sm text-zinc-600">
                  Your feedback is aggregated into training signals. Future RFPs in your category will be more accurate.
                </p>
              </div>
            </div>

            <div className="bg-white/60 rounded-xl p-5 border border-purple-100">
              <div className="flex items-start gap-4">
                <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-zinc-900 mb-2">What Gets Learned?</h4>
                  <ul className="grid md:grid-cols-2 gap-2">
                    <li className="flex items-center gap-2 text-sm text-zinc-600">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span>Terminology preferences (vendor → supplier)</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-zinc-600">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span>Category-specific questions</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-zinc-600">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span>Scope & evaluation criteria patterns</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-zinc-600">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span>Compliance requirements by domain</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="text-xs text-purple-700 mt-6 text-center">
              All feedback is anonymized and used solely to improve RFP generation quality. Your procurement data remains private.
            </p>
          </div>
        </div>

        {/* Choice & Flexibility Section */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-zinc-900 mb-3">Choice & Flexibility</h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Every organization has different requirements. ProcMatrix lets you choose the AI model that best fits your needs, compliance requirements, and preferences.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {aiModels.map((model) => (
              <div 
                key={model.name}
                className="bg-white border border-zinc-200 rounded-xl p-6 text-center hover:border-[#0055FF] hover:shadow-md transition-all duration-300"
              >
                <div className="text-4xl mb-4">{model.logo}</div>
                <h3 className="font-semibold text-zinc-900 mb-2">{model.name}</h3>
                <p className="text-sm text-zinc-600">{model.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-zinc-500">
              Configure your preferred AI provider in settings • Switch anytime based on your needs
            </p>
          </div>
        </div>

        {/* Smart Intake vs Quick Intake Section */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-zinc-900 mb-3">Two Ways to Start</h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Choose the intake method that matches your situation—whether you need speed or comprehensive analysis.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick Intake */}
            <div className="bg-gradient-to-br from-[#0055FF]/5 to-[#0055FF]/10 border-2 border-[#0055FF]/30 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 relative">
              <div className="absolute top-4 right-4">
                <span className="text-xs font-bold text-white bg-[#0055FF] px-3 py-1 rounded-full">RECOMMENDED</span>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Gauge className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-zinc-900">Quick Intake</h3>
                  <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Fast Track</span>
                </div>
              </div>
              
              <p className="text-zinc-600 mb-6">
                Jump straight into the procurement process when you already have clear requirements. Ideal for repeat purchases or well-defined needs.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600">Direct requirement entry</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600">Faster time to RFP generation</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600">Best for experienced buyers</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600">Standard question generation</span>
                </div>
              </div>
            </div>

            {/* Smart Intake - disabled for beta, kept in place for easy re-enable later */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-8 opacity-50 relative">
              <div className="absolute top-4 right-4">
                <span className="text-xs font-bold text-white bg-zinc-500 px-3 py-1 rounded-full">COMING SOON</span>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-zinc-300 rounded-xl">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-zinc-900">Smart Intake</h3>
                  <span className="text-xs font-medium text-[#0055FF] bg-[#0055FF]/10 px-2 py-0.5 rounded">AI-Guided</span>
                </div>
              </div>
              
              <p className="text-zinc-600 mb-6">
                Let specialized AI agents analyze your requirements, identify gaps, and guide you through a comprehensive intake process.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0055FF] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-700"><strong>Gap Analysis:</strong> Found, Inferred & Missing details</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0055FF] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-700"><strong>Domain Experts:</strong> IT & Facilities specialized agents</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0055FF] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-700"><strong>Smart Routing:</strong> Auto-assigns to right buyer agent</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0055FF] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-700"><strong>Master Data:</strong> Pre-filled from your org&apos;s data</span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-[#0055FF]/20">
                <p className="text-xs text-zinc-500">
                  Smart Intake uses Router Agent → Specialized Buyer Agents → Gap Analysis flow to ensure complete requirements before RFP generation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Security Features Section */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-zinc-900 mb-3">Enterprise-Grade Security</h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Your procurement data is sensitive. ProcMatrix is built with security at its core.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-zinc-200 rounded-xl p-6 hover:shadow-md transition-all">
              <div className="p-2 bg-green-100 rounded-lg w-fit mb-4">
                <ShieldCheck className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-zinc-900 mb-2">Google OAuth</h3>
              <p className="text-sm text-zinc-600">
                Secure authentication via Google—no passwords stored in our system.
              </p>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-6 hover:shadow-md transition-all">
              <div className="p-2 bg-blue-100 rounded-lg w-fit mb-4">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-zinc-900 mb-2">MongoDB Atlas</h3>
              <p className="text-sm text-zinc-600">
                Enterprise cloud database with encryption at rest and in transit.
              </p>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-6 hover:shadow-md transition-all">
              <div className="p-2 bg-purple-100 rounded-lg w-fit mb-4">
                <Lock className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-zinc-900 mb-2">Admin RBAC</h3>
              <p className="text-sm text-zinc-600">
                Role-based access control separates admin and user privileges.
              </p>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-6 hover:shadow-md transition-all">
              <div className="p-2 bg-amber-100 rounded-lg w-fit mb-4">
                <KeyRound className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="font-semibold text-zinc-900 mb-2">API Key Security</h3>
              <p className="text-sm text-zinc-600">
                AI provider keys are stored securely and never exposed to clients.
              </p>
            </div>
          </div>

          <div className="mt-6 bg-zinc-50 border border-zinc-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Eye className="w-6 h-6 text-zinc-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-zinc-900 mb-2">Data Privacy</h3>
                <p className="text-zinc-600 text-sm">
                  Your procurement data is processed solely to provide ProcMatrix&apos;s procurement services, including requirement understanding, procurement intelligence, risk assessment, and procurement document generation. ProcMatrix does not share, sell, or use your organization&apos;s procurement data to train public AI models.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Info className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">AI Processing Notice</h3>
                <p className="text-amber-800">
                  ProcMatrix uses a commercial AI API to generate procurement outputs.
                  Requirement information submitted for AI analysis is processed by the
                  configured AI provider. Organizations should avoid submitting
                  information that is prohibited by their internal data handling policies
                  during the beta evaluation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Current Beta Scope Section */}
        <div className="mb-16">
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-zinc-200 rounded-lg flex-shrink-0">
                <Info className="w-5 h-5 text-zinc-600" />
              </div>
              <div className="space-y-4 w-full">
                <h2 className="text-2xl font-bold text-zinc-900">Current Beta Scope</h2>
                <p className="text-zinc-600">During this Beta release, ProcMatrix supports:</p>
                <ul className="space-y-2">
                  {betaScopeItems.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-zinc-600">
                      <CheckCircle2 className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-zinc-600">
                  The Beta program focuses on validating procurement workflows, document quality, and AI-assisted requirement understanding. Additional capabilities and expanded scalability will be introduced in future releases.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* About this Beta */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-12">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Info className="w-5 h-5 text-amber-600" />
            </div>
            <div className="space-y-3">
              <h3 className="font-semibold text-amber-900">About this Beta</h3>
              <p className="text-amber-800">
                ProcMatrix is currently in beta. Features may evolve based on your feedback.
              </p>
              <p className="text-amber-800">
                One role, all features: during beta, every user sees the complete platform — requester intake, buyer workflows, risk intelligence, and master data administration — so you can explore end-to-end functionality. In production, capabilities are separated by role (requester, buyer, administrator) with appropriate access controls.
              </p>
              <p className="text-amber-800">
                For questions or support, contact <a href="mailto:ashokr@sahinta.com" className="underline">ashokr@sahinta.com</a>
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-zinc-900 rounded-2xl p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Transform Your Procurement Process?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
            Start your first AI-powered procurement request and see how ProcMatrix can save your team hours of manual work.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/new')}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-8 py-6 text-lg"
            >
              Create New Procurement
            </Button>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-800 px-8 py-6 text-lg"
            >
              View Dashboard
            </Button>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default About;
